import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, MapPin, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '$lib/components/ui/Badge';
import { Button } from '$lib/components/ui/Button';
import { resolveAddress } from '$lib/api/client';

// /search?q=… — the address-search-as-target landing.

export function AddressSearchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const { t } = useTranslation();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['address-resolve', q],
    queryFn: () => resolveAddress(q),
    enabled: q.length > 0
  });

  useEffect(() => {
    if (!data) return;
    const next = `/units/${data.target.unitId}`;
    navigate(next, { replace: true });
  }, [data, navigate]);

  if (!q) {
    return (
      <Wrap>
        <EmptyState />
      </Wrap>
    );
  }

  if (isPending) {
    return (
      <Wrap>
        <div role="status" className="flex items-center gap-2 text-xs text-fg-muted">
          <Search size={14} />
          {t('addressSearch.resolving')} <span className="text-fg">{q}</span>…
        </div>
      </Wrap>
    );
  }

  if (isError) {
    return (
      <Wrap>
        <div role="alert" className="flex flex-col items-center gap-2 text-center">
          <AlertTriangle className="text-danger" size={24} />
          <p className="text-sm text-fg">{t('addressSearch.couldNotResolve')}</p>
          <p className="text-2xs text-fg-muted">{(error as Error)?.message ?? t('addressSearch.unknownError')}</p>
        </div>
      </Wrap>
    );
  }

  if (!data) {
    return (
      <Wrap>
        <NoMatchState query={q} />
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="flex flex-col gap-3">
        <p className="text-2xs text-fg-muted">{t('addressSearch.resolved')}</p>
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-fg-muted" />
          <Link to={`/units/${data.target.unitId}`} className="text-sm text-fg hover:text-accent">
            {data.target.address}
          </Link>
          {data.target.kind === 'provisional' && (
            <Badge tone="warning">{t('unitDetail.provisionalLiveBbr')}</Badge>
          )}
        </div>
        {data.candidates.length > 0 && (
          <div className="mt-3 rounded-sm border border-rule bg-surface-2 p-2 text-2xs">
            <p className="text-fg-subtle">{t('addressSearch.otherMatches')}</p>
            <ul className="mt-1 space-y-0.5">
              {data.candidates.map((c) => (
                <li key={c.unitId}>
                  <Link to={`/units/${c.unitId}`} className="text-fg hover:text-accent">
                    {c.address}
                    <span className="num ml-2 text-fg-subtle">{c.postnummer}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Wrap>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-1 items-start justify-center p-12" data-testid="address-search-page">
      <div className="w-full max-w-md rounded-sm border border-rule bg-surface p-4 shadow-elev-1">
        {children}
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="text-center">
      <Search className="mx-auto text-fg-muted" size={20} />
      <h2 className="mt-2 text-sm font-semibold text-fg">{t('addressSearch.title')}</h2>
      <p className="mt-1 text-2xs text-fg-muted">
        {t('addressSearch.intro')}
      </p>
    </div>
  );
}

function NoMatchState({ query }: { query: string }) {
  const { t } = useTranslation();
  const template = t('addressSearch.noMatchBody', { query });
  // Split <strong>{{query}}</strong> placeholder so we can render the
  // emphasised query inline without a separate <Trans> dep.
  const open = '<strong>';
  const close = '</strong>';
  const oi = template.indexOf(open);
  const ci = template.indexOf(close);
  let body: React.ReactNode = template;
  if (oi !== -1 && ci !== -1) {
    body = (
      <>
        {template.slice(0, oi)}
        <span className="text-fg">{template.slice(oi + open.length, ci)}</span>
        {template.slice(ci + close.length)}
      </>
    );
  }
  return (
    <div className="text-center">
      <AlertTriangle className="mx-auto text-warning" size={20} />
      <h2 className="mt-2 text-sm font-semibold text-fg">{t('addressSearch.noMatchTitle')}</h2>
      <p className="mt-1 text-2xs text-fg-muted">{body}</p>
      <Button size="sm" variant="primary" className="mt-3" onClick={() => history.back()}>
        {t('addressSearch.goBack')}
      </Button>
    </div>
  );
}
